/**
 * A deliberately small Markdown subset for client-supplied long-form text (privacy statements,
 * terms): "## " and "### " headings, paragraphs, "- " lists, **bold** and [links](url).
 * Everything else is escaped, so client text can never inject HTML.
 *
 * Help pages (`renderHelpMarkdown`) also get "1. " numbered lists, links to portal screens ("#/jobs") and links
 * to other help pages ("help:jobs").
 */

const escapeHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const SAFE_HREF = /^(https?:\/\/|mailto:|tel:|\/)/;

interface Options {
  /** "1. " lines make a numbered list. */
  ordered?: boolean;
  /** Map a link target the default rules refuse (return null to leave the text as written). */
  href?: (href: string) => string | null;
}

function inline(text: string, opts: Options = {}): string {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, label: string, href: string) => {
      const url = href.replace(/&amp;/g, "&");
      const mapped = SAFE_HREF.test(url) ? url : (opts.href?.(url) ?? null);
      return mapped ? `<a href="${escapeHtml(mapped)}">${label}</a>` : m;
    });
}

/** Render the subset to HTML. `headingOffset` shifts heading levels (1 turns "##" into h3). */
export function renderMarkdown(src: string, headingOffset = 0, opts: Options = {}): string {
  const out: string[] = [];
  let para: string[] = [];
  let list: string[] = [];
  let numbered: string[] = [];
  const flush = () => {
    if (para.length) out.push(`<p>${inline(para.join(" "), opts)}</p>`);
    if (list.length) out.push(`<ul>${list.map((i) => `<li>${inline(i, opts)}</li>`).join("")}</ul>`);
    if (numbered.length) out.push(`<ol>${numbered.map((i) => `<li>${inline(i, opts)}</li>`).join("")}</ol>`);
    para = [];
    list = [];
    numbered = [];
  };
  for (const raw of src.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trim();
    const heading = /^(#{2,4})\s+(.*)$/.exec(line);
    if (!line) {
      flush();
    } else if (heading) {
      flush();
      const level = Math.min(6, heading[1]!.length + headingOffset);
      out.push(`<h${level}>${inline(heading[2]!, opts)}</h${level}>`);
    } else if (/^[-*]\s+/.test(line)) {
      if (para.length || numbered.length) flush();
      list.push(line.replace(/^[-*]\s+/, ""));
    } else if (opts.ordered && /^\d{1,3}[.)]\s+/.test(line)) {
      if (para.length || list.length) flush();
      numbered.push(line.replace(/^\d{1,3}[.)]\s+/, ""));
    } else {
      if (list.length || numbered.length) flush();
      para.push(line);
    }
  }
  flush();
  return out.join("\n");
}

/** Help pages: numbered lists, portal screen links and help:page links (to "#/help/<page>"). */
export function renderHelpMarkdown(src: string, headingOffset = 0): string {
  return renderMarkdown(src, headingOffset, {
    ordered: true,
    href: (h) => (/^#\/[a-z0-9/-]*$/.test(h) ? h : /^help:[a-z0-9-]+$/.test(h) ? `#/help/${h.slice(5)}` : null),
  });
}
